import type { FandomTaxonomyTargeting } from "./types";
import type { GptWrapper } from "./../../core/GptWrapper";
import { waitFor, waitWithTimeout } from "utils";
import { AdEnginePlugin, type Hooks } from "./../../core/AdEnginePlugin";
import { getIABCategories } from "./getters/getIABCategories";
import { getTaxonomyKeyValues } from "./getters/getTaxonomyKeyValues";
import { IAB_CATTAX } from "./consts";

const WAIT_FOR_FANDOM_TAXONOMY_TIMEOUT = 500;

interface FandomTaxonomyPluginAPI {
  /** Returns IAB categories for the current page */
  getIABPageCategories: () => string[];

  /** Returns IAB categories for the current site */
  getIABSiteCategories: () => string[];

  /** Returns all taxonomy key values (with proper prefixes and suffixes, as expected by GAM) */
  getTargetingKeyValues: () => FandomTaxonomyTargeting;
}

/**
 * FandomTaxonomyTargetingPlugin is responsible for getting parameters from fandomTaxonomy
 * It reads global `fandomTaxonomy` and exposes API to get IAB categories and targeting key values
 * from it.

* It also sets the targeting for Google Publisher Tag (GPT) using the `onAfterGptWrapperInit` hook.
 */
export class FandomTaxonomyPlugin extends AdEnginePlugin<FandomTaxonomyPluginAPI> {
  // Ensure the GPT targeting is only set once
  private targetingApplied = false;

  /**
   * Promise that resolves when FandomTaxonomyUpdated event is fired.
   * That event is added to the payload that is returned from FandomTaxonomy service.
   */
  private async waitForEvent(): Promise<void> {
    return new Promise<void>((resolve) => {
      const handler = () => resolve();

      window.addEventListener("FandomTaxonomyUpdated", () => {
        window.removeEventListener("FandomTaxonomyUpdated", handler);
        resolve();
      });
    });
  }

  /**
   * Resolves when fandomTaxonomy is present on the page.
   *
   * There are few ways in which that promise can resolve:
   * - window.fandomTaxonomy is already present on the page (it's loaded before we started waiting)
   * - fandomTaxonomy appears on the page (it's loaded after we started waiting)
   * - FandomTaxonomyUpdated event is fired (it's loaded after we started waiting)
   * - timeout (fandomTaxonomy is not loaded at all)
   */
  private async waitForFandomTaxonomyPresent(): Promise<void> {
    if (window.fandomTaxonomy) {
      return;
    }

    try {
      await waitWithTimeout(
        Promise.any([
          waitFor(() => !!window.fandomTaxonomy),
          this.waitForEvent(),
        ]),
        WAIT_FOR_FANDOM_TAXONOMY_TIMEOUT,
      );
    } catch {
      console.warn("waitForFandomTaxonomyPresent timed out");
    }
  }

  /**
   * Updates config for Google Publisher Tag (GPT) with IAB categories.
   *
   * @param gptWrapper GptWrapper instance
   */
  private updateGptConfig(gptWrapper: GptWrapper): void {
    // https://github.com/Wikia/ad-engine/blob/939da2d5336a664921fda5cdfd235a06cfe9c044/src/core/services/fandom-taxonomy-service.ts#L175-L188
    gptWrapper.setConfig({
      pps: {
        taxonomies: {
          IAB_CONTENT_2_2: {
            values: [
              ...new Set([
                ...getIABCategories("site"),
                ...getIABCategories("page"),
              ]),
            ],
          },
        },
      },
    });
  }

  /**
   * Returns IAB categories for the current page and site, and CATTAX.
   * The values are set as key-value pairs for Google Publisher Tag (GPT).
   */
  private getIABKeyValues(): Record<string, string[]> {
    return {
      // not ideal, but let's not duplicate CATTAX and import it from OpenRtbPlugin
      cattax: [IAB_CATTAX.toString()],
      cat: getIABCategories("site"),
      pagecat: getIABCategories("page"),
    };
  }

  /**
   * Updates key-values for Google Publisher Tag (GPT) with taxonomy key values and IAB categories.
   *
   * @param gptWrapper GptWrapper instance
   */
  private updateGptKVs(gptWrapper: GptWrapper): void {
    Object.entries({
      ...this.getIABKeyValues(),
      ...getTaxonomyKeyValues(),
    }).forEach(([key, value]) => {
      // get value from gptWrapper
      const gptValue = gptWrapper.getTargeting(key);
      // if value is not empty, merge it with the new value
      if (gptValue && gptValue.length > 0) {
        value = [...new Set([...gptValue, ...value])];
      }
      // set the value in gptWrapper
      gptWrapper.setTargeting(key, value);
    });
  }

  /**
   * Sets the values for Google Publisher Tag (GPT).
   *
   * @param gptWrapper GptWrapper instance
   */
  private updateGpt(gptWrapper: GptWrapper): void {
    if (this.targetingApplied) {
      return;
    }
    this.targetingApplied = true;

    this.updateGptKVs(gptWrapper);
    this.updateGptConfig(gptWrapper);
  }

  hooks: Hooks = {
    onAfterGptWrapperInit: (gptWrapper: GptWrapper) => {
      // by the time this hook is called, fandomTaxonomy should be present already
      // if that isn't enough we may consider waiting for fandomTaxonomy in the core (like with host)
      this.waitForFandomTaxonomyPresent().then(() =>
        this.updateGpt(gptWrapper),
      );
    },
  };

  api = {
    getIABPageCategories: () => getIABCategories("page"),
    getIABSiteCategories: () => getIABCategories("site"),
    getTargetingKeyValues: () => getTaxonomyKeyValues(),
  };
}
